import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { OrganizationsNavigationProvider } from '../../../organizations/providers/navigation-provider';
import { ConfirmDialogComponent } from '../../../common/components/confirm-dialog/confirm-dialog';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { AthletesFormComponent } from '../../components/form/form';
import { AthleteRow, AthletesProvider } from '../../providers/athletes-provider';
import { CoachesProvider, CoachRow } from '../../../coaches/providers/coaches-provider';
import { TeamsProvider } from '../../../teams/providers/teams-provider';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { AthletesCoachesFormComponent } from '../../components/form/coaches';
import { AthletesTeamsFormComponent } from '../../components/form/teams';
import { AthleteRights } from '../../providers/athletes-rights';
import { UsersProvider } from '../../../users/providers/users-provider';
import { forkJoin, Observable, of, Subscription, switchMap, takeUntil, throwError } from 'rxjs';
import { MatSort, MatSortHeader, MatSortModule } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModuleMenu } from "../../../common/models/menu";
import { Organization } from "../../../common/models/organization";
import { Gender } from "../../../common/models/gender";
import { ContractType } from "../../../common/models/contract-type";
import { Coach } from "../../../common/models/coach";
import { Team } from "../../../common/models/team";
import { SimpleMatDataSource } from "../../../common/providers/simple-mat-data-source";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { ConfigProvider } from "../../../config-provider.service";
import { catchError, map } from "rxjs/operators";
import { User } from "../../../common/models/user";
import { Athlete } from "../../../common/models/athlete";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { MatFormField, MatSuffix } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import {
  MatCell, MatCellDef, MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from "@angular/material/table";
import { NgFor, NgIf, NgOptimizedImage } from "@angular/common";
import { MatInput } from "@angular/material/input";
import { MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { Paginator } from "../../../common/models/paginator";
import { ApiPaginator } from "../../../common/components/api-paginator/api-paginator";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";

/**
 * NOTE (Yves) : Je considère que, pour le moment, un athlète possède une seule adresse. Le modèle
 * de la base de données permet d'avoir plusieurs adresses, mais le client ne l'a pas demandé.
 * L'interface de création/édition de l'athlète intègre la modification de son adresse. Lors
 * de la prochaine évolution du logiciel, il faudra adapter les écrans si le clients souhaite
 * gérer plusieurs adresses pour un athlète (cf. adresse de facturation par exemple).
 */
@Component({
  selector: 'athletes-list-page',
  providers: [],
  styleUrls: ['./list.scss'],
  standalone: true,
  imports: [
    Error404Component,
    ModuleContentComponent,
    HeaderComponent,
    PageContentComponent,
    Error401Component,
    MatFormField,
    MatSuffix,
    MatIcon,
    MatMenu,
    MatHeaderRow,
    MatRow,
    MatTable,
    MatCell,
    MatHeaderCell,
    NgIf,
    MatRowDef,
    MatSortModule,
    TranslateModule,
    MatInput,
    MatHeaderRowDef,
    MatColumnDef,
    MatMenuTrigger,
    MatMenuItem,
    MatCellDef,
    MatIconButton,
    MatHeaderCellDef,
    MatTooltip,
    MatSortHeader,
    NgOptimizedImage,
    ApiPaginator,
    CenterLoaderComponent,
  ],
  templateUrl: './list.html'
})
export class AthletesListPageComponent implements AfterViewInit, OnDestroy {

    public DESCRIPTION_MAX_LENGTH = 5000;

    public pageTitle = 'Dashboard';
    public mainActions: ModuleMenu[] = [];
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public activePage: number = 1;
    public activeOrderBy: string = 'name_asc';

    public organization: Organization  | null = null;
    public availableGenders: Gender[] = [];
    public availableContractTypes: ContractType[] = [];
    public availableCoaches: Coach[] = [];
    public availableTeams: Team[] = [];
    public athletesPaginator: Paginator = {} as Paginator;

    public rights: AthleteRights = new AthleteRights(this._utils);
    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public columns: string[] = ['avatar', 'name', 'coachs', 'teams', 'actions'];
    private userFilter = '';
    private coachFilter = '';

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;
    @ViewChild(MatSort, {static: false}) public sorter: MatSort | undefined;

    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        public currentUser: CurrentUser,
        public breadcrumbProvider: BreadcrumbProvider,
        private _utils: UtilsProvider,
        private _title: Title,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private _router: Router,
        private _dialoger: MatDialog,
        private _toaster: MatSnackBar,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _route: ActivatedRoute,
        private _translater: TranslateService,
        private _dialog: MatDialog,
        private _athletesProvider: AthletesProvider,
        private _teamsProvider: TeamsProvider,
        private _coachesProvider: CoachesProvider,
        private _usersProvider: UsersProvider
    ) {
    }

    public ngAfterViewInit() {
      this._route.params.pipe(
        takeUntil(this.subscriptions),
        switchMap(() => this.loadOrganization()), // Assuming loadOrganization returns an Observable
        catchError((error) => {
          this.onError(error);
          return of(null); // Handle the error and continue the stream with a null value
        })
      ).subscribe({
        next: () => {
          this.initPage();
        },
        error: (err) => this.onError(err) // Additional error handling if needed
      });
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public onDisplayAthleteClicked(toView: AthleteRow) {
      const url = '/' + this.organization!.code + '/athletes/' + toView.user.id;
      this._router.navigateByUrl(url);
    }

    public onAddAthleteClicked(): void {
        forkJoin([
          this.loadCoaches(),
          this.loadTeams()
        ]).subscribe({
          next: () => {
            const dialogRef: any = this._dialog.open(AthletesFormComponent, {
              data: {
                organization: this.organization,
                availableGenders: this.availableGenders,
                availableContractTypes: this.availableContractTypes,
                availableCoaches: this.availableCoaches,
                availableTeams: this.availableTeams,
                formTitle: 'Athlete_creation',
                confirmButtonTitle: this._translater.instant('Save')
              }
            });

            // màj de la liste
            dialogRef.afterClosed().subscribe((row: AthleteRow) => {
              if (row) {
                this.setAthletesRows({}).subscribe();
                // this._athletesProvider.addImageToRow(this.organization.id, row);
              }
            });
          },
          error: (error) => this.onError(error)
        });

    }

    public onModifyAthleteClicked(toModify: AthleteRow): void {
      forkJoin([
        this.loadCoaches(),
        this.loadTeams()
      ]).subscribe({
        next: () => {
          const dialogRef: any = this._dialog.open(AthletesFormComponent, {
              data: {
                  organization: this.organization,
                  availableGenders: this.availableGenders,
                  availableContractTypes: this.availableContractTypes,
                  availableCoaches: this.availableCoaches,
                  availableTeams: this.availableTeams,
                  athleteRow: toModify,
                  formTitle: 'Athlete_edition',
                  confirmButtonTitle: this._translater.instant('Save')
              }
          });

          // màj de la liste
          this.subscriptions.push(
              dialogRef.afterClosed().subscribe((row: AthleteRow) => {
                  if (row) {
                      for (let i = 0; i < this.dataSource.count; i++) {
                          if (this.dataSource.get(i).athlete.id === row.athlete.id) {
                              this.dataSource.set(i, row);
                              // this._athleteProvider.addImageToRow(this.organization.id, row);
                              return;
                          }
                      }
                  }
              })
          );
        },
        error: (error) => this.onError(error)
      });

    }

    public onManageCoachesClicked(toModify: AthleteRow): void {
        this.loadCoaches().subscribe({
          next: () => {
            const dialogRef: any = this._dialog.open(AthletesCoachesFormComponent, {
              data: {
                organization: this.organization,
                availableCoaches: this.availableCoaches,
                athleteRow: toModify,
                formTitle: 'Athlete_Coach_edition',
                confirmButtonTitle: this._translater.instant('Save')
              }
            });

            // màj de la liste
            this.subscriptions.push(
              dialogRef.afterClosed().subscribe((row: AthleteRow) => {
                if (row) {
                  for (let i = 0; i < this.dataSource.count; i++) {
                    if (this.dataSource.get(i).athlete.id === row.athlete.id) {
                      this.dataSource.set(i, row);
                      return;
                    }
                  }
                }
              })
            );
          },
          error: (error) => this.onError(error)
        });
    }

    public onManageTeamsClicked(toModify: AthleteRow): void {
      this.loadTeams().subscribe({
        next: () => {
          const dialogRef: any = this._dialog.open(AthletesTeamsFormComponent, {
              data: {
                  organization: this.organization,
                  availableTeams: this.availableTeams,
                  athleteRow: toModify,
                  formTitle: 'Athlete_Team_edition',
                  confirmButtonTitle: this._translater.instant('Save')
              }
          });

          // màj de la liste
          this.subscriptions.push(
              dialogRef.afterClosed().subscribe((row: AthleteRow) => {
                  if (row) {
                      for (let i = 0; i < this.dataSource.count; i++) {
                          if (this.dataSource.get(i).athlete.id === row.athlete.id) {
                              this.dataSource.set(i, row);
                              return;
                          }
                      }
                  }
              })
          );
        },
        error: (error) => this.onError(error)
      });
    }

    public onEnableAthleteClicked(toEnable: AthleteRow, active: boolean): void {
        this.enableAthlete(toEnable.athlete, active).subscribe({
          next: () => {
            for (let i = 0; i < this.dataSource.count; i++) {
              if (this.dataSource.get(i).athlete.id === toEnable.athlete.id) {
                toEnable.user.active = +active;
                this.dataSource.set(i, toEnable);
                break;
              }
            }
            this.isLoading = false;
            this._toaster.open(this._translater.instant(active ? 'User_enabled' : 'User_disabled'), '', {duration: 5000});
          },
          error: (error) => {
            this.onError(error);
          },
        });
    }

    public onDeleteAthleteClicked(toDelete: AthleteRow): void {

        const dialog: MatDialogRef<any> = this._dialoger.open(ConfirmDialogComponent, {
            data: {
                confirmText: this._translater.instant('Confirm_delete_athlete')
            }
        });

        this.subscriptions.push(
            dialog.afterClosed().subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.isLoading = true;
                    this.deleteAthlete(toDelete.athlete).subscribe({
                        next: () => {
                            this.isLoading = false;
                            this._toaster.open(this._translater.instant('Athlete_deleted'), '', {duration: 5000});
                        },
                        error: (error: any) => this.onError(error)
                    });
                }
            })
        );
    }

    public onSortChange(event: any): void {
      if (event.active === 'name') {
        this.setAthletesRows({orderBy: `name_${event.direction}`}).subscribe();
      }
        this.dataSource.sortBy(event.active, event.direction);
    }

    public onUserFilterKeyup(filter: string): void {
      // a new search always restarts on the first page: keeping the active page
      // requests a page past the filtered results and shows an empty list (#476)
      this.setAthletesRows({userName: filter, page: 1}).subscribe({
        error: (error) => this.onError(error),
      });
    }

    public onCoachFilterKeyup(filter: string): void {
      this.setAthletesRows({coachName: filter, page: 1}).subscribe({
        error: (error) => this.onError(error),
      });
    }

    private loadOrganization(): Observable<any> {
         return   this._organizationNavigation.getOrganisationFromRoute().pipe(
              map((organization: Organization | null) => {
                    if (organization) {
                        this.organization = organization;
                    } else {
                        throw({error: 'Organization not found', status: 404});
                    }
                })
         );
    }

    private initPage(): void {
      this.loadData().subscribe({
        next: () => {
          this.initNav();
          this.initSorters();
          this.initSearch();
          this.dataSource.sortBy('name', 'asc');
          this.isLoading = false;
        },
        error: (error) => this.onError(error)
      });
    }

    private initNav(): void {

        if (!this.rights.LIST) {
            this.is401 = true;
            return;
        }

        if (this.rights.SEND_INVITATION) {
            this.columns = ['avatar', 'name', 'coachs', 'teams', 'notvalidate', 'actions'];
        }

        this.dataSource.paginator = this.paginator!;

        if (this.rights.ADD) {
            this.mainActions = [
                {title: 'Add', icon: 'add', url: null, onClick: () => this.onAddAthleteClicked()}
            ];
        }

        this.createPageTitle();
        this.createBreadcrumb();
    }

    private initSorters(): void {
        this.dataSource.addSortDefinition(
            'name',
            (a: AthleteRow, b: AthleteRow) => this._utils.sortRow((`${a.user.firstName} ${a.user.lastName}`).toLowerCase(), (`${b.user.firstName} ${b.user.lastName}`).toLowerCase()),
            (a: AthleteRow, b: AthleteRow) => this._utils.sortRow((`${b.user.firstName} ${b.user.lastName}`).toLowerCase(), (`${a.user.firstName} ${a.user.lastName}`).toLowerCase())
        );
    }

    private initSearch(): void {
        this.dataSource.sort = this.sorter!;
        this.dataSource.filterPredicate = (data: AthleteRow, filter: string) => {
            const userStr: string = this._utils.normalizeString(`${data.user.firstName} ${data.user.lastName}`);
            const coachStr: string = this._utils.normalizeString(data.coachNames);
            const userPredicate = userStr.includes(this.userFilter.replace(/\s/g, ''));
            const coachPredicate = coachStr.includes(this.coachFilter.replace(/\s/g, ''));
            return userPredicate && coachPredicate;
        };
    }

    private loadData(): Observable<any> {
      if (!this.organization) {
        return throwError(() => new Error('Organization not found'));
      }
      return forkJoin([
        this._athletesProvider.getGenders(),
        this._athletesProvider.getContractTypes(),
        this.setAthletesRows({}),
        of(this._currentUserRoles.getRolesIds(this.organization.id!)),
      ]).pipe(
        map(([genders, contractTypes, athletes, roleIds]) => {
          this.availableGenders = genders ?? [];
          this.availableContractTypes = contractTypes ?? [];

          this.rights.evaluateScreenRights({
            rolesIds: roleIds,
            limitedCoach: this.currentUser.user?.organizationMemberships
              .find((membership) => membership.organizationId === this.organization?.id)?.isLimited
          });
        }),
      );
    }

    public pageChange(page: number): void {
      this.isLoading = true;
      this.setAthletesRows({page: page}).subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error) => {
          this.onError(error);
        },
      });
    }

    private setAthletesRows({orderBy = this.activeOrderBy, page = this.activePage, userName = this.userFilter, coachName = this.coachFilter}: {orderBy?: string, page?: number, userName?: string, coachName?: string}): Observable<void> {
      return this._athletesProvider.getAthletesPaginator({
        organizationId: this.organization?.id!,
        orderBy: orderBy,
        pageNumber: page,
        userName: userName,
        coachName: coachName,
      }).pipe(
        map((athletesPaginator: Paginator) => {
          // Build the rows before publishing any state: a row that fails to build must not
          // leave the paginator showing a count for rows the table never receives (#476).
          const athletes: AthleteRow[] = athletesPaginator.data.map((user: User) =>
            this._athletesProvider.createAthleteRow(this.organization?.id!, user));

          this.activePage = page;
          this.activeOrderBy = orderBy;
          this.userFilter = userName;
          this.coachFilter = coachName;
          athletesPaginator.data = athletes;
          this.athletesPaginator = athletesPaginator;
          this.dataSource.replaceAllWith(athletes);

          this.dataSource.paginator = this.paginator!;
        })
      );
    }

    public onResendInvitationClicked(user: User) {

        this.isLoading = true;
        this._usersProvider.sendInvitation(user).subscribe({
            next: () => {
                this.isLoading = false;
                this._toaster.open(this._translater.instant('Invitation_sent'), '', { duration: 5000 });
            },
            error: () => {
                this.isLoading = false;
                this._toaster.open(this._translater.instant('Invitation_send_error'), '', { duration: 5000 });
            }
        });
    }

    private enableAthlete(athlete: Athlete, active: boolean): Observable<any> {
      if (!athlete.userId) {
        this.onError({error: 'User ID not found', status: 404});
        return of(null);
      }
      return this._usersProvider.updateUser(athlete.userId, {'active': active});
    }

    private deleteAthlete(athlete: Athlete): Observable<void> {
      if (!athlete.userId) {
        this.onError({error: 'User ID not found', status: 404});
        return of();
      }
      return this._athletesProvider.deleteAthlete(this.organization?.id!, athlete.userId).pipe(
        map(() => {
          for (let i = 0; i < this.dataSource.count; i++) {
            if (this.dataSource.get(i).athlete.id === athlete.id) {
              this.dataSource.remove(i);
              i = this.dataSource.count;
            }
          }
        }),
      );
    }

    private createPageTitle(){
        this.pageTitle = this._translater.instant('Athletes_list');
        this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {

        const crumbs: Crumb[] = [{
            shortTitle: 'Athletes',
            title: 'Athletes',
            url: this._router.url
        }];
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 404) {
            this.is404 = true;
        } else if (error.status === 401) {
            this.is401 = true;
        }
    }

    private loadCoaches(): Observable<void> {
      return this._coachesProvider.getCoaches(this.organization!.id!).pipe(
        map((coaches: Coach[]) => {
          this.availableCoaches = coaches;
        }),
        catchError((error) => {
          this.onError(error);
          this.availableCoaches = [];
          return of(void 0);
        }),
      );
    }

    private loadTeams(): Observable<void> {
      return this._teamsProvider.getTeams({organizationId: this.organization?.id!}).pipe(
        map((teams: Team[]) => {
          this.availableTeams = teams;
        }),
        catchError((error) => {
          this.onError(error);
          this.availableCoaches = [];
          return of(void 0);
        }),
      );
    }
}
